import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Building2, BarChart3, Scale, Cpu, Home, TrendingUp, Shield, Award, Lightbulb, Wrench, ChevronRight, ChevronLeft, Send, Linkedin, Mail, Monitor, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ArticleContent from "@/components/ArticleContent";
import { newsApi, newsPath, mediaUrl, formatNewsDate } from "@/lib/newsApi";
import { teamMembers } from "@/lib/teamData";
import api from "@/lib/api";

const W = "max-w-[1200px] mx-auto px-6 lg:px-10";

const services = [
  { icon: Building2, title: "Sviluppo immobiliare", desc: "Sviluppo immobiliare integrato in ambito metropolitano, con interventi su nuove edificazioni e riqualificazioni ad alta redditivita.", color: "#20c997" },
  { icon: BarChart3, title: "Asset Management & Advisory", desc: "Valorizziamo patrimoni immobiliari tramite perizie, ottimizzazione dei rendimenti e gestione trasparente del rischio.", color: "#FF6B6B" },
  { icon: Scale, title: "Perequazione e diritti edificatori", desc: "Pionieri a Milano nell'uso della perequazione per valorizzare immobili in aree di trasformazione urbanistica.", color: "#FFA94D" },
  { icon: Cpu, title: "Proptech", desc: "Tecnologia e digitalizzazione al servizio dell'efficienza operativa e gestionale del settore immobiliare.", color: "#748FFC" },
  { icon: Home, title: "Hospitality e Student Living", desc: "Progetti integrati di housing per studenti, lavoratori e anziani. Gestione condomini e agenzia immobiliare integrata nel gruppo.", color: "#F783AC" },
  { icon: TrendingUp, title: "Private Equity & Venture Capital", desc: "Investiamo in societa e startup con alto potenziale e progetti innovativi per il mercato di riferimento.", color: "#63E6BE" },
];

const strengths = [
  { icon: Shield, kicker: "Track record", title: "Operazioni concrete", desc: "Sviluppi residenziali, asset management e vendita diretta. Relazioni trasparenti, obiettivi condivisi con clienti e partner." },
  { icon: Award, kicker: "Tradizione", title: "Tre generazioni", desc: "Una storia imprenditoriale lunga oltre un secolo nel mondo delle costruzioni e del real estate. Esperienza, metodo, continuita." },
  { icon: Lightbulb, kicker: "Data-driven", title: "Metodo istituzionale", desc: "Business plan, cashflow e monitoraggio in tempo reale su ogni operazione. La trasparenza che le aziende strutturate si aspettano." },
  { icon: Wrench, kicker: "Su misura", title: "Pied-a-terre al masterplan", desc: "Operiamo dal piccolo intervento di pregio al masterplan urbano. Stessi standard, stessa cura sartoriale a ogni scala." },
];

const companies = [
  { name: "PX Group", tagline: "Holding operativa e manageriale", initials: "PX", color: "#0f292f", desc: "PX Group e la holding operativa e manageriale del gruppo: qui nascono le idee, si costruiscono le strategie, si avviano i progetti. E il punto d'incontro tra visione imprenditoriale, direzione strategica e capacita operativa." },
  { name: "LeaseH", tagline: "Student living reimmaginato", initials: "LH", color: "#748FFC", desc: "LeaseH rivoluziona il concetto di student housing con soluzioni abitative moderne, efficienti e pensate per le esigenze degli studenti nell'area metropolitana milanese." },
  { name: "PQuadro", tagline: "Innovazione nel real estate", initials: "P2", color: "#FF6B6B", desc: "PQuadro porta innovazione e visione strategica nel real estate, sviluppando progetti che ridefiniscono gli standard del settore immobiliare." },
  { name: "Home Deal", tagline: "Investimenti agili, ritorni reali", initials: "HD", color: "#FFA94D", desc: "Home Deal si specializza in operazioni immobiliari agili e mirate, con focus sulla massimizzazione dei ritorni per gli investitori." },
  { name: "Archint", tagline: "Lusso tra architettura e sviluppo", initials: "AI", color: "#F783AC", desc: "Archint unisce architettura di lusso e sviluppo immobiliare di alto profilo, creando progetti esclusivi che definiscono nuovi standard di eleganza." },
  { name: "Pettirosso", tagline: "Immobili a reddito con amore", initials: "PT", color: "#63E6BE", desc: "Pettirosso nasce come family office dedicato agli immobili a reddito, progettati con amore e gestiti con efficienza. Un approccio intimo e curato al real estate di qualita." },
];

const FEATURED_TEAM_SLUGS = ["stefano-perani", "giovanni-perani", "federico-giuliano"];
const team = FEATURED_TEAM_SLUGS.map((s) => teamMembers.find((m) => m.slug === s)).filter(Boolean);

const stats = [
  { value: "40+", label: "Operazioni" },
  { value: "€100M+", label: "AUM gestiti" },
  { value: "50.000", label: "MQ Sviluppati" },
  { value: "30.000", label: "MQ Diritti edificatori transati" },
  { value: "20.000", label: "MQ Under management" },
  { value: "1913", label: "Anno di fondazione" },
];

const awards = [
  {
    label: "Leader della Crescita",
    source: "Il Sole 24 Ore × Statista",
    years: "2019 · 2020 · 2021",
    logo: "/awards/il-sole-24-ore.svg",
    logoAlt: "Il Sole 24 Ore",
    link: "https://lab24.ilsole24ore.com/leader-crescita/",
  },
  {
    label: "FT 1000 Europe's Fastest Growing Companies",
    source: "Financial Times × Statista",
    years: "2021",
    logo: "/awards/financial-times.svg",
    logoAlt: "Financial Times",
    link: "https://www.ft.com/ft1000-2021",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", request_type: "info", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [storyExpanded, setStoryExpanded] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);
  const newsRef = useRef(null);

  useEffect(() => {
    newsApi.list().then((d) => setNewsArticles(d.slice(0, 12))).catch(() => setNewsArticles([]));
  }, []);

  // Handle #anchor scroll when navigating from other pages (e.g. /team/xxx -> /#team)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wait for the DOM to render (especially for lazy-loaded sections)
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.hash]);

  const handleContact = async (e) => {
    e.preventDefault(); setSending(true);
    try { await api.post("/contact", contactForm); setSent(true); setContactForm({ name: "", email: "", phone: "", request_type: "info", message: "" }); } catch (err) { alert("Errore nell'invio. Riprova."); }
    setSending(false);
  };

  const scrollNews = (dir) => { if (newsRef.current) newsRef.current.scrollBy({ left: dir * 340, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-[#f0f0ef]" data-testid="home-page">
      <Navbar />

      {/* ─── Hero ─── */}
      {/*
        ALTERNATIVE HERO COPY — uncomment one to swap. Default = V1 (Atelier — sogni → progetti).

        V2 — Manifesto secco :
          kicker:  "— Real Estate Investment & Development"
          h1:      "Diamo forma alle case che durano."
          sub:     "Sviluppo, asset management e investimenti immobiliari. Per chi compra una casa, per chi investe in un progetto."

        V3 — Asciutto / poetico :
          kicker:  "— Atelier del real estate"
          h1:      "Dai sogni ai progetti."
          sub:     "Sviluppiamo, gestiamo e vendiamo immobili di pregio. Dal pied-a-terre al masterplan urbano."
      */}
      <section className="relative -mt-px" data-testid="hero-section">
        <div className="relative w-full overflow-hidden" style={{ height: "clamp(560px, 84vh, 780px)" }}>
          {/* Foto abstract architettonica — full-bleed, edge-to-edge */}
          <img
            src="https://images.unsplash.com/photo-1780857496705-de4c5fa36e33?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-bottom"
            style={{ transform: "scaleX(-1.05) scaleY(1.05)" }}
          />
          {/* Overlay cinematografico: lato sinistro molto scuro/pulito dietro al testo,
              l'architettura resta visibile a destra */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-transparent" />
          {/* Vignette dal basso per profondità */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          {/* Grain leggerissimo per feel editoriale */}
          <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay pointer-events-none"
               style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

          {/* Contenuto — allineato a sinistra dentro un container leggibile */}
          <div className="relative h-full max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-14">
            <div className="h-full flex flex-col justify-end pb-14 sm:pb-20 max-w-2xl">
              <span className="inline-flex items-center gap-3 kicker-num text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-medium text-[#20c997] mb-6 animate-hero-in [animation-delay:0ms]">
                <span className="w-8 h-px bg-[#20c997]" />
                Atelier del Real Estate
              </span>
              <h1 className="font-display text-[2.4rem] sm:text-[3.6rem] lg:text-[4.4rem] text-white leading-[1.02] mb-6 sm:mb-8 tracking-tight animate-hero-in [animation-delay:120ms]">
                Dai sogni <span className="font-display-soft italic text-white/90">ai progetti</span>,<br className="hidden sm:block" />
                dai progetti <span className="font-display-soft italic text-white/90">alle case</span>.
              </h1>
              <p className="text-base sm:text-[17px] text-white/80 max-w-[560px] leading-relaxed mb-9 sm:mb-10 font-light animate-hero-in [animation-delay:240ms]">
                Sviluppiamo, gestiamo e vendiamo immobili di pregio. Per chi cerca una casa,
                per chi investe in un progetto, per chi affida un patrimonio.
              </p>
              <div className="flex flex-row items-center gap-3 sm:gap-4 mb-8 animate-hero-in [animation-delay:360ms]">
                <button
                  onClick={() => document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-[#20c997] text-[#0a1f24] font-semibold px-6 sm:px-7 py-3 sm:py-3.5 rounded-full hover:bg-[#12b886] hover:shadow-lg hover:shadow-[#20c997]/20 transition-all flex items-center gap-2 text-sm sm:text-base"
                  data-testid="hero-contact-btn"
                >
                  Parla con noi <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => document.getElementById("progetti")?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-white/8 backdrop-blur-md text-white font-medium px-6 sm:px-7 py-3 sm:py-3.5 rounded-full hover:bg-white/15 transition-all flex items-center gap-2 text-sm sm:text-base border border-white/25"
                  data-testid="hero-projects-btn"
                >
                  Vedi i progetti <ChevronRight size={16} />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/55 max-w-2xl animate-hero-in [animation-delay:480ms]" data-testid="hero-awards">
                {awards.map((a) => (
                  <div key={a.label} className="flex items-center gap-1.5 text-[10px] sm:text-[11px]">
                    <Award size={11} className="text-[#20c997]/80" />
                    <span className="font-medium tracking-tight text-white/80">{a.label}</span>
                    <span className="text-white/40">— {a.source} {a.years}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Riconosciuti da (loghi stampa ufficiali) ─── */}
      <section className="bg-white py-7 sm:py-14 border-y border-[#0f292f]/8" data-testid="press-logos-section">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <p className="text-center overline mb-5 sm:mb-8 text-[10px]">Riconosciuti da</p>
          <div className="grid grid-cols-3 items-center justify-items-center gap-2 sm:gap-12">
            {/* Il Sole 24 Ore - Leader della Crescita */}
            <a
              href="https://lab24.ilsole24ore.com/leader-crescita/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center w-full"
              title="Leader della Crescita - Il Sole 24 Ore × Statista 2019-2022"
              data-testid="press-logo-sole24ore"
            >
              <img
                src="/press/sole24ore.png"
                alt="Il Sole 24 Ore - Leader della Crescita"
                loading="lazy"
                decoding="async"
                className="h-10 sm:h-24 w-auto object-contain opacity-90 group-hover:opacity-100 sm:group-hover:scale-105 transition-[opacity,transform] duration-300"
              />
              <span className="text-[8px] sm:text-[10px] text-[#0f292f]/50 mt-1.5 sm:mt-3 tracking-wide font-medium text-center leading-tight">Leader Crescita</span>
            </a>

            {/* Financial Times 1000 */}
            <a
              href="https://www.ft.com/ft1000-2021"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center w-full"
              title="FT 1000 Europe's Fastest Growing Companies - Financial Times × Statista 2021"
              data-testid="press-logo-ft"
            >
              <img
                src="/press/ft.png"
                alt="FT 1000 Europe's Fastest Growing Companies"
                loading="lazy"
                decoding="async"
                className="h-10 sm:h-24 w-auto object-contain opacity-90 group-hover:opacity-100 sm:group-hover:scale-105 transition-[opacity,transform] duration-300"
              />
              <span className="text-[8px] sm:text-[10px] text-[#0f292f]/50 mt-1.5 sm:mt-3 tracking-wide font-medium text-center leading-tight">FT 1000</span>
            </a>

            {/* Statista */}
            <a
              href="https://www.statista.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center w-full"
              title="Statista - certifying body"
              data-testid="press-logo-statista"
            >
              <img
                src="/press/statista.png"
                alt="Statista"
                loading="lazy"
                decoding="async"
                className="h-4 sm:h-10 w-auto object-contain opacity-80 group-hover:opacity-100 sm:group-hover:scale-105 transition-[opacity,transform] duration-300"
              />
              <span className="text-[8px] sm:text-[10px] text-[#0f292f]/50 mt-1.5 sm:mt-3 tracking-wide font-medium text-center leading-tight">Certifying</span>
            </a>
          </div>
        </div>
      </section>

      {/* ─── PX Group + Storia (merged) ─── */}
      <section id="about" className="py-14 sm:py-16 bg-white" data-testid="about-section">
        <div className={W}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-stretch">
            {/* Foto grande "architettura pop" — su mobile in alto, su desktop a destra */}
            <div className="order-1 lg:order-2 relative rounded-3xl overflow-hidden h-[300px] sm:h-[420px] lg:h-auto lg:min-h-[540px] shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1780809551503-f85acb1962b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Architettura contemporanea"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center absolute inset-0 transition-transform duration-700 group-hover:scale-105 will-change-transform"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f292f]/55 via-transparent to-transparent" />
              <span className="absolute bottom-5 left-5 text-white text-xs font-medium tracking-wide bg-[#0f292f]/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/15">
                Real estate contemporaneo
              </span>
            </div>
            {/* Testo: PX Group + La nostra storia, insieme */}
            <div className="order-2 lg:order-1 flex flex-col justify-center">
              <span className="overline">PX Group</span>
              <h2 className="font-display text-3xl sm:text-[2.6rem] text-[#0f292f] mt-4 mb-6 leading-[1.05]">Sviluppiamo, gestiamo e <span className="font-display-soft">vendiamo</span> immobili di pregio.</h2>
              <p className="text-base text-[#555] leading-relaxed mb-4">PX Group e un gruppo italiano specializzato in real estate. Sviluppiamo nuove costruzioni e riqualificazioni a Milano e nelle principali piazze italiane, vendiamo direttamente unita ai privati e gestiamo patrimoni per investitori e family office con un metodo <span className="font-medium text-[#0f292f]">data-driven</span> e visione di lungo periodo.</p>
              <p className="text-base text-[#555] leading-relaxed">Una struttura snella e selettiva, costruita per seguire ogni operazione con la stessa cura sartoriale. Per chi compra una casa, per chi investe in un progetto.</p>
              <div className="border-t border-gray-200 pt-6 mt-7">
                <h3 className="text-xl font-semibold text-[#0f292f] mb-4">La nostra storia</h3>
                <p className="text-base text-[#555] leading-relaxed mb-3">Le nostre origini risalgono al 1913, con la fondazione di uno stabilimento di cementi e calcestruzzi nella bergamasca. Da allora, generazioni di ingegneri hanno portato avanti questa tradizione imprenditoriale.</p>
                <p className={`text-base text-[#555] leading-relaxed ${storyExpanded ? "block" : "hidden sm:block"}`}>Nel 2004 nasce a Milano Italimmobiliare. Nel 2011 viene fondata Milano Costruzioni. Da queste fondamenta nasce PX Group: una struttura agile, professionale e selettiva, costruita per seguire ogni operazione con la massima attenzione.</p>
                {!storyExpanded && (
                  <button
                    onClick={() => setStoryExpanded(true)}
                    className="sm:hidden mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#20c997] hover:text-[#1bb583] transition-colors"
                    data-testid="story-continue-btn"
                  >
                    Continua a leggere <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Servizi ─── */}
      <section id="servizi" className="py-14 sm:py-16 bg-[#fafafa]" data-testid="services-section">
        <div className={W}>
          <div className="text-center mb-8 sm:mb-12"><span className="overline">Di che cosa ci occupiamo</span><h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 leading-[1.05]">I nostri <span className="font-display-soft">servizi</span></h2></div>
          {/* MOBILE: card orizzontali compatte (row) · SM+: grid classica */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {services.map((s, i) => (
              <div key={s.title} className="bg-white rounded-2xl p-4 sm:p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex sm:block items-start gap-4" data-testid={`service-card-${i}`}>
                <div className="w-11 h-11 sm:w-14 sm:h-14 shrink-0 rounded-xl flex items-center justify-center sm:mb-6" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon className="w-5 h-5 sm:w-[26px] sm:h-[26px]" style={{ color: s.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-[#0f292f] mb-1 sm:mb-3 leading-tight">{s.title}</h3>
                  <p className="text-[13px] sm:text-base text-[#666] leading-snug sm:leading-relaxed line-clamp-3 sm:line-clamp-none">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Perche noi (DARK) — pillars qualitativi senza numeri ─── */}
      <section className="py-14 sm:py-16 bg-[#0f292f]" data-testid="strengths-section">
        <div className={W}>
          <div className="text-center mb-10 sm:mb-12">
            <span className="overline">I nostri punti di forza</span>
            <h2 className="font-display text-3xl sm:text-[2.5rem] text-white mt-4 leading-[1.05]">Perche <span className="font-display-soft">scegliere</span> noi</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
            {strengths.map((s) => (
              <div key={s.title} className="text-left sm:text-center flex sm:block items-start gap-3 sm:gap-0 sm:px-2" data-testid={`strength-${s.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="w-9 h-9 sm:w-12 sm:h-12 shrink-0 sm:mx-auto sm:mb-5 rounded-lg sm:rounded-xl bg-[#20c997]/10 flex items-center justify-center">
                  <s.icon size={16} className="text-[#20c997] sm:hidden" />
                  <s.icon size={22} className="text-[#20c997] hidden sm:block" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-semibold text-[#20c997]/90 block mb-0.5 sm:mb-2">{s.kicker}</span>
                  <h3 className="text-[14px] sm:text-lg lg:text-xl font-display text-white leading-tight mb-0.5 sm:mb-3">{s.title}</h3>
                  <p className="text-[11.5px] sm:text-sm text-white/55 leading-snug sm:leading-relaxed font-light line-clamp-2 sm:line-clamp-none">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Ecosistema ─── */}
      <section id="gruppo" className="py-14 sm:py-16 bg-white" data-testid="group-section">
        <div className={W}>
          <div className="text-center mb-10 sm:mb-12">
            <span className="overline">Le nostre societa</span>
            <h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 mb-4 leading-[1.05]">Un ecosistema <span className="font-display-soft">integrato</span></h2>
            <p className="text-base text-[#666] max-w-xl mx-auto">Dalla grande operazione al dettaglio di nicchia, trasformiamo spazi in opportunita.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {companies.map((c) => (
              <button
                key={c.name}
                onClick={() => setSelectedCompany(c)}
                className="group relative bg-white border border-[#0f292f]/8 rounded-2xl p-4 sm:p-6 hover:border-[#20c997]/40 hover:shadow-[0_8px_30px_-12px_rgba(15,41,47,0.15)] transition-all text-left overflow-hidden"
                data-testid={`company-${c.name.replace(/\s/g, '-').toLowerCase()}`}
              >
                {/* Color accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1 transition-all duration-300 group-hover:h-1.5" style={{ backgroundColor: c.color }} />
                {/* MOBILE: layout orizzontale compatto · SM+: layout classico */}
                <div className="flex sm:block items-center gap-3 sm:gap-4">
                  <div className="flex sm:mb-4 items-start sm:items-center gap-3 sm:gap-4 sm:justify-between shrink-0 sm:shrink w-auto sm:w-full">
                    <div
                      className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl flex items-center justify-center text-white text-xs sm:text-sm font-bold transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: c.color }}
                    >
                      {c.initials}
                    </div>
                    <ChevronRight size={16} className="hidden sm:block text-[#ccc] group-hover:text-[#20c997] group-hover:translate-x-1 transition-all shrink-0 ml-auto" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[15px] sm:text-lg font-bold text-[#0f292f] group-hover:text-[#20c997] transition-colors mb-0.5 sm:mb-1.5 leading-tight">
                      {c.name}
                    </h3>
                    <p className="text-[12px] sm:text-sm text-[#888] leading-snug sm:leading-relaxed line-clamp-1 sm:line-clamp-none">{c.tagline}</p>
                  </div>
                  <ChevronRight size={16} className="sm:hidden text-[#ccc] group-hover:text-[#20c997] shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Numeri ─── */}
      <section className="py-14 sm:py-16 bg-[#0f292f]" data-testid="numbers-section">
        <div className={W}>
          <div className="text-center mb-10 sm:mb-12">
            <span className="overline">I numeri del gruppo</span>
            <h2 className="font-display text-3xl sm:text-[2.5rem] text-white mt-4 leading-[1.05]">Risultati concreti, <span className="font-display-soft">costruiti nel tempo</span>.</h2>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-8">{stats.map((s) => (<div key={s.label} className="text-center py-1 sm:py-3"><div className="font-display text-xl sm:text-3xl lg:text-4xl font-medium text-[#20c997] mb-1 sm:mb-2 leading-none tracking-tight" style={{ fontVariantNumeric: "tabular-nums" }}>{s.value}</div><div className="text-[9px] sm:text-[11px] text-white/50 uppercase tracking-[0.14em] sm:tracking-[0.18em] font-medium leading-tight">{s.label}</div></div>))}</div>
        </div>
      </section>

      {/* ─── La Bottega — software signature ─── */}
      <section className="py-14 sm:py-16 bg-[#f5f3ee]" data-testid="bottega-section">
        <div className={W}>
          <div className="text-center mb-10 sm:mb-12">
            <span className="overline">Software proprietario</span>
            <h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 leading-[1.05]">
              Il nostro <span className="font-display-soft">cuore operativo</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left on desktop: software showcase — Apple Studio Display mockup with CRM inside */}
            <div className="order-1">
              <div className="relative" data-testid="crm-mockup-studio">
                <div className="absolute -inset-6 bg-gradient-to-tr from-[#20c997]/12 via-[#20c997]/5 to-transparent rounded-[2.5rem] blur-2xl" aria-hidden="true" />
                <img
                  src="/crm-mockup.webp"
                  alt="Software proprietario PX Group — dashboard CRM su Apple Studio Display"
                  className="relative w-full block drop-shadow-2xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-xs text-[#0f292f]/50 mt-3 text-center">— Dashboard principale, €100M+ AUM monitorati</p>
            </div>

            {/* Right on desktop: editorial copy */}
            <div className="order-2">
              <p className="text-lg sm:text-xl text-[#0f292f]/85 leading-[1.4] mb-7 border-l-2 border-[#20c997] pl-5 font-light">
                Ogni operazione e viva. Asking, margine, capex, cashflow — in tempo reale. E il motivo per cui i nostri investitori dormono tranquilli.
              </p>
              <p className="text-[15px] text-[#555] leading-relaxed mb-3">PX Group ha sviluppato internamente una piattaforma proprietaria per la gestione e il monitoraggio di ogni singola operazione. Non un CRM generico, ma il <strong className="font-medium text-[#0f292f]">cuore operativo</strong> con cui seguiamo ogni metro quadro, ogni euro di capex, ogni timeline.</p>
              <p className="text-[15px] text-[#555] leading-relaxed mb-6">Asset management, business plan istituzionali, P&amp;L, cashflow, gestione documentale e data room dedicate per ogni club deal — costruito da chi conosce il settore dall'interno.</p>
              <div className="flex flex-wrap gap-2">
                {["Asset Management", "Business Plan", "Cashflow", "Club Deal Data Room", "P&L", "Permessi & SAL"].map((t) => (
                  <span key={t} className="px-3 py-1.5 bg-white text-[#0f292f] text-[11px] font-medium rounded-full border border-[#0f292f]/8">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Progetti ─── */}
      <section id="progetti" className="py-14 sm:py-16 bg-[#fafafa]" data-testid="projects-section">
        <div className={W}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="overline">I nostri progetti</span>
              <h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 mb-4 leading-[1.05]">Ogni sogno ha una <span className="font-display-soft">forma</span></h2>
              <p className="text-base text-[#555] leading-relaxed mb-6">Dalle residenze urbane innovative alle riqualificazioni d'avanguardia: in citta, in montagna e tra i mari. Progettiamo e sviluppiamo con attenzione a ogni dettaglio.</p>
              <button onClick={() => navigate("/projects")} className="bg-[#0f292f] text-white font-medium px-8 py-3.5 rounded-full hover:bg-[#0f292f]/85 transition-all inline-flex items-center gap-2" data-testid="projects-explore-btn">Esplora tutti i progetti <ArrowRight size={16} /></button>

              <div className="mt-8 pt-6 border-t border-[#0f292f]/10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#20c997] mb-2">In vendita ora</p>
                <Link to="/delfico-district" className="group inline-flex items-center gap-3 bg-white border border-[#0f292f]/10 hover:border-[#20c997]/50 rounded-2xl px-4 py-3 transition-all" data-testid="delfico-featured-link">
                  <div>
                    <p className="text-sm font-bold text-[#0f292f]">Delfico District</p>
                    <p className="text-[11px] text-[#666]">Ghisolfa · da € 299.000 · Classe A</p>
                  </div>
                  <ArrowRight size={16} className="text-[#20c997] group-hover:translate-x-1 transition-transform ml-1" />
                </Link>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{ height: "400px" }}>
              <img src="/hero/progetti-milano.webp" alt="Progetti PX Group" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>


      {/* ─── Team ─── */}
      <section id="team" className="py-14 sm:py-16 bg-white" data-testid="team-section">
        <div className={W}>
          <div className="text-center mb-10 sm:mb-12">
            <span className="overline">Team</span>
            <h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 leading-[1.05]">Le persone dietro <span className="font-display-soft">PX Group</span></h2>
          </div>
          <div className="grid grid-cols-3 gap-4 sm:gap-6">
            {team.map((t) => (
              <Link
                key={t.name}
                to={`/team/${t.slug}`}
                className="group text-center cursor-pointer flex flex-col"
                data-testid={`team-member-${t.name.replace(/\s/g, '-').toLowerCase()}`}
              >
                <div className="relative w-full aspect-square overflow-hidden rounded-2xl bg-[#e8e8e8] flex-shrink-0">
                  {t.img ? <img src={t.img} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-500 will-change-transform" /> : (
                    <div className="w-full h-full flex items-center justify-center bg-[#0f292f]">
                      <img src="/logo.png" alt="PX Group" loading="lazy" className="w-1/2 h-auto opacity-90" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-[#0f292f]/0 group-hover:bg-[#0f292f]/20 transition-[background-color] duration-300 flex items-center justify-center"><span className="text-white text-xs sm:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-[#0f292f]/60 px-3 py-1.5 rounded-full">Vedi profilo</span></div>
                </div>
                <div className="h-[52px] flex flex-col justify-start mt-3">
                  <h3 className="text-xs sm:text-base font-bold text-[#0f292f] leading-tight">{t.name}</h3>
                  <p className="text-[10px] sm:text-sm text-[#999]">{t.role}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10 sm:mt-12">
            <Link to="/team" className="inline-flex items-center gap-2 bg-[#0f292f] text-white text-sm font-semibold px-7 py-3 rounded-full hover:bg-[#0f292f]/90 transition-colors" data-testid="team-see-all">
              Vedi tutto il team
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── News Carousel ─── */}
      <section id="news" className="py-14 sm:py-16 bg-[#fafafa]" data-testid="news-section">
        <div className={W}>
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <span className="overline">News</span>
              <h2 className="font-display text-3xl sm:text-[2.5rem] text-[#0f292f] mt-4 leading-[1.05]">Ultime <span className="font-display-soft">novita</span></h2>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Link to="/news" className="hidden sm:inline text-sm font-semibold text-[#0f292f] hover:text-[#20c997] transition-colors" data-testid="news-see-all">Tutte le news →</Link>
              <div className="flex gap-2">
                <button onClick={() => scrollNews(-1)} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-white transition-colors" data-testid="news-prev"><ChevronLeft size={18} /></button>
                <button onClick={() => scrollNews(1)} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-white transition-colors" data-testid="news-next"><ChevronRight size={18} /></button>
              </div>
            </div>
          </div>
          <div ref={newsRef} className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
            <style>{`.news-scroll::-webkit-scrollbar{display:none}`}</style>
            {newsArticles.map((n, i) => (
              <Link to={newsPath(n)} key={n.article_id} className="flex-shrink-0 w-[280px] sm:w-[300px] snap-start group cursor-pointer text-left flex flex-col" data-testid={`news-card-${i}`}>
                <div className="rounded-2xl overflow-hidden h-[200px] flex-shrink-0 bg-[#f0f0ef] flex items-center justify-center">
                  {n.image_url ? <img src={mediaUrl(n.image_url)} alt={n.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 will-change-transform" /> : <img src="/logo.png" alt="PX Group" className="w-14 h-14 opacity-30" />}
                </div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#20c997] font-semibold mt-4">{formatNewsDate(n.date)}</p>
                <h3 className="text-base font-bold text-[#0f292f] group-hover:text-[#20c997] transition-colors leading-snug mt-1.5 h-[48px] overflow-hidden">{n.title}</h3>
              </Link>
            ))}
          </div>
          <div className="sm:hidden mt-4"><Link to="/news" className="text-sm font-semibold text-[#0f292f] hover:text-[#20c997] transition-colors" data-testid="news-see-all-mobile">Tutte le news →</Link></div>
        </div>
      </section>

      {/* ─── Contatti ─── */}
      <section id="contatti" className="py-10 sm:py-16 bg-white" data-testid="contact-section">
        <div className={W}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
            <div>
              <span className="overline">Contatti</span>
              <h2 className="font-display text-2xl sm:text-[2.5rem] text-[#0f292f] mt-3 sm:mt-4 mb-3 sm:mb-6 leading-[1.05]">Parliamone, <span className="font-display-soft">davanti a un caffe</span>.</h2>
              <p className="text-[13px] sm:text-base text-[#555] leading-relaxed mb-2 sm:mb-3">Le nostre opportunita di co-investimento sono riservate a investitori privati qualificati e family office.</p>
              <p className="hidden sm:block text-sm text-[#888] italic leading-relaxed mb-8">Lasciateci due righe e vi richiameremo entro 48 ore.</p>
              <div className="flex items-center gap-3 mt-4 sm:mt-0">
                <img src="/logo.png" alt="PX" loading="lazy" decoding="async" className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl object-cover" />
                <div>
                  <p className="font-bold text-[#0f292f] text-sm sm:text-base">PX Group S.R.L.</p>
                  <p className="text-[12px] sm:text-sm text-[#666]">Piazza Castello, 24 - 20121 Milano MI</p>
                </div>
              </div>
            </div>
            <div>
              {sent ? (
                <div className="bg-[#fafafa] rounded-2xl p-10 text-center"><div className="w-14 h-14 rounded-full bg-[#20c997]/10 flex items-center justify-center mx-auto mb-4"><Send size={22} className="text-[#20c997]" /></div><h3 className="text-lg font-bold text-[#0f292f] mb-2">Messaggio inviato!</h3><p className="text-base text-[#666]">Ti ricontatteremo al piu presto.</p></div>
              ) : (
                <form onSubmit={handleContact} className="space-y-3 sm:space-y-4 bg-[#fafafa] rounded-2xl p-5 sm:p-8">
                  <input type="text" placeholder="Nome e Cognome" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="input-public" data-testid="contact-name-input" />
                  <div className="grid grid-cols-2 gap-3"><input type="email" placeholder="Email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="input-public" data-testid="contact-email-input" /><input type="tel" placeholder="Telefono" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="input-public" data-testid="contact-phone-input" /></div>
                  <select value={contactForm.request_type} onChange={(e) => setContactForm({ ...contactForm, request_type: e.target.value })} className="input-public" data-testid="contact-type-select"><option value="info">Chiedi informazioni</option><option value="lavora">Lavora con noi</option></select>
                  <textarea placeholder="Messaggio" rows={3} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="input-public resize-none" data-testid="contact-message-input" />
                  <button type="submit" disabled={sending} className="w-full bg-[#20c997] text-white font-medium py-3 sm:py-3.5 rounded-xl hover:bg-[#1bb583] transition-all flex items-center justify-center gap-2" data-testid="contact-submit-btn"><Send size={16} />{sending ? "Invio..." : "Invia"}</button>
                  <p className="text-[10px] sm:text-xs text-[#999] text-center mt-1 sm:mt-2">Inviando accetti le condizioni <button type="button" onClick={() => setShowPrivacy(true)} className="text-[#20c997] underline hover:text-[#1bb583] transition-colors" data-testid="privacy-link">privacy</button></p>
                  <div className="flex items-center gap-3 pt-1"><div className="flex-1 h-px bg-gray-200" /><span className="text-[10px] text-gray-400 uppercase tracking-wider">oppure</span><div className="flex-1 h-px bg-gray-200" /></div>
                  <a href="https://wa.me/390297376589" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="w-full inline-flex items-center justify-center gap-2 border border-[#25D366] text-[#128C7E] font-medium py-3 rounded-xl hover:bg-[#25D366]/10 transition-all text-sm">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2z"/></svg>
                    Scrivici su WhatsApp
                  </a>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Closing manifesto ─── */}
      <section className="bg-[#0f292f] py-14 sm:py-16 border-t border-white/5" data-testid="signature-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-display text-2xl sm:text-3xl lg:text-[2.2rem] text-white/90 leading-[1.25] font-light">
            PX Group e un gruppo italiano <span className="text-[#20c997]">di sviluppo immobiliare</span>. Costruiamo case <span className="font-medium">fatte bene</span>, per chi sa scegliere.
          </p>
          <p className="kicker-num text-white/40 text-xs mt-7 tracking-[0.2em] uppercase">— Tradizione dal 1913</p>
        </div>
      </section>

      <Footer />

      {/* ─── Company Modal ─── */}
      <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
        <DialogContent className="bg-white border border-gray-200 text-[#0f292f] max-w-md p-0 overflow-hidden rounded-2xl">
          <DialogTitle className="sr-only">{selectedCompany?.name || "Dettaglio"}</DialogTitle>
          {selectedCompany && (<div className="p-6"><div className="flex items-center gap-4 mb-5"><div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: selectedCompany.color }}>{selectedCompany.initials}</div><div><h3 className="text-xl font-bold text-[#0f292f]">{selectedCompany.name}</h3><p className="text-sm text-[#20c997] font-medium">{selectedCompany.tagline}</p></div></div><p className="text-base text-[#555] leading-relaxed">{selectedCompany.desc}</p></div>)}
        </DialogContent>
      </Dialog>

      {/* ─── Article Modal ─── */}
      <Dialog open={!!selectedArticle} onOpenChange={() => setSelectedArticle(null)}>
        <DialogContent className="bg-white border border-gray-200 text-[#0f292f] max-w-2xl p-0 overflow-hidden rounded-2xl">
          {selectedArticle && (
            <div className="overflow-y-auto" style={{ maxHeight: "85vh" }}>
              <DialogTitle className="sr-only">{selectedArticle.title}</DialogTitle>
              {selectedArticle.image_url && <img src={mediaUrl(selectedArticle.image_url)} alt={selectedArticle.title} loading="lazy" decoding="async" className="w-full h-[280px] object-cover object-top" />}
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/logo.png" alt="PX" loading="lazy" decoding="async" className="w-8 h-8 rounded object-cover" />
                  <div>
                    <p className="text-sm font-bold text-[#0f292f]">{selectedArticle.author || "PX Group"}</p>
                    <p className="text-xs text-[#999]">{formatNewsDate(selectedArticle.date)}</p>
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#0f292f] leading-tight mb-6" data-testid="article-modal-title">{selectedArticle.title}</h2>
                <ArticleContent text={selectedArticle.content} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Privacy Modal ─── */}
      <Dialog open={showPrivacy} onOpenChange={() => setShowPrivacy(false)}>
        <DialogContent className="bg-white border border-gray-200 text-[#0f292f] max-w-2xl p-0 overflow-hidden rounded-2xl">
          <DialogTitle className="sr-only">Informativa Privacy</DialogTitle>
          <div className="overflow-y-auto" style={{ maxHeight: "85vh" }}>
            <div className="p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-[#0f292f] mb-6" data-testid="privacy-modal-title">Informativa Privacy</h2>
              <div className="text-sm text-[#444] leading-[1.85] space-y-4">
                <p>Con riferimento al trattamento dei dati degli utenti che consultano il sito pxgroup.it (il "Sito") PXGroup ai sensi e per gli effetti di cui agli articoli 13 e 14 del Regolamento UE 2016/679, fornisce la seguente informativa:</p>
                <h3 className="text-base font-bold text-[#0f292f] mt-4">Tipologia di dati trattati e finalita:</h3>
                <h4 className="font-bold text-[#0f292f]">DATI DI NAVIGAZIONE</h4>
                <p>I sistemi informatici e le procedure software utilizzate per il funzionamento del Sito acquisiscono, nel corso del loro normale esercizio, alcuni dati personali la cui trasmissione e implicita nell'uso della rete Internet. Si tratta di informazioni che non sono raccolte per essere associate a interessati identificati, ma che per loro stessa natura potrebbero, attraverso elaborazioni e associazioni con dati detenuti da terzi, permettere di identificare gli utenti navigatori. Tali dati potranno essere utilizzati al fine di ricavare informazioni statistiche anonime sull'uso del Sito, per controllarne il corretto funzionamento o per l'eventuale accertamento di responsabilita in caso di reati informatici ai danni del Sito.</p>
                <h4 className="font-bold text-[#0f292f]">DATI FORNITI VOLONTARIAMENTE DALL'UTENTE</h4>
                <p>I dati forniti in modo volontario dagli utenti del Sito inoltrati a PXGroup tramite posta elettronica, inclusi gli indirizzi e-mail, saranno acquisiti e trattati al solo scopo di fornire le informazioni e/o i servizi richiesti. In caso di invio di dati personali di terzi soggetti inviati dagli utenti a PXGroup, l'utente si pone come autonomo titolare del trattamento, assumendosi tutti gli obblighi e le responsabilita di legge.</p>
                <h4 className="font-bold text-[#0f292f]">COOKIE</h4>
                <p>Il Sito fa uso di cookie cosi come meglio specificato nell'apposita informativa.</p>
                <h4 className="font-bold text-[#0f292f]">MODALITA DEL TRATTAMENTO E MISURE DI SICUREZZA</h4>
                <p>I dati personali acquisiti e forniti saranno trattati con strumenti automatizzati e non, per il solo tempo strettamente necessario a conseguire gli scopi per cui sono stati raccolti. Specifiche misure di sicurezza sono osservate per prevenire la perdita dei dati, usi illeciti o non corretti e accessi non autorizzati.</p>
                <h4 className="font-bold text-[#0f292f]">COMUNICAZIONE DEI DATI</h4>
                <p>Ferme restando le comunicazioni eseguite in adempimento di obblighi di legge, tutti i dati raccolti ed elaborati potranno essere comunicati per le finalita di cui sopra a: i) alla societa PXGroup ed alle societa del gruppo; ii) soggetti che possono venirne a conoscenza, in qualita di "responsabili" o "incaricati" di PXGroup.</p>
                <h4 className="font-bold text-[#0f292f]">TITOLARE E RESPONSABILE DEL TRATTAMENTO</h4>
                <p>Titolare del trattamento dei dati e PXGroup, con sede legale in via Manzoni 43, 20121, Milano C.F. e P.IVA 09375210961 e il responsabile del trattamento e il Rappresentante Legale e Amministratore Delegato di PXGroup, Simona Casini.</p>
                <h4 className="font-bold text-[#0f292f]">DIRITTI DEGLI INTERESSATI</h4>
                <p>L'interessato cui si riferiscono i dati personali ha il diritto in qualunque momento di ottenere la conferma dell'esistenza o meno dei medesimi dati e di conoscerne il contenuto e l'origine, verificarne l'esattezza o chiederne l'integrazione o l'aggiornamento, oppure la rettifica. L'interessato ha il diritto di chiedere la cancellazione, la trasformazione in forma anonima, nonche di opporsi in ogni caso, per motivi legittimi, al loro trattamento.</p>
                <h4 className="font-bold text-[#0f292f]">MODIFICHE ALL'INFORMATIVA PRIVACY</h4>
                <p>PXGroup potra apportare modifiche alla presente Informativa Privacy al fine di recepire cambiamenti della normativa nazionale e/o comunitaria ovvero per adeguarsi ad intervenute innovazioni tecnologiche o per altri motivi. L'utente e pregato di prendere visione della presente Informativa Privacy e di controllarla periodicamente.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
